import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { get3plRateCard, upsert3plRateCard } from '../../lib/api';
import { fmtDate } from './utils';

export default function RateCardTab({ selected }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!selected) { setCard(null); setForm(null); return; }
    setLoading(true);
    get3plRateCard(selected.id)
      .then((r) => {
        const data = r.data;
        if (data?.no_card) {
          setCard(null);
          setForm({
            currency: 'USD',
            storage: { per_pallet_per_month: 25, per_bin_per_month: 0, per_cubic_meter_per_month: 0, free_pallets: 0 },
            inbound: { per_receipt: 5, per_pallet: 8, per_carton: 0.5 },
            outbound: { per_order: 1.5, per_pick_line: 0.4, per_shipment: 2.0 },
            vas: [],
            minimum_monthly: 0,
          });
        } else {
          setCard(data);
          setForm({
            currency: data.currency || 'USD',
            storage: data.storage || { per_pallet_per_month: 0, per_bin_per_month: 0, per_cubic_meter_per_month: 0, free_pallets: 0 },
            inbound: data.inbound || { per_receipt: 0, per_pallet: 0, per_carton: 0 },
            outbound: data.outbound || { per_order: 0, per_pick_line: 0, per_shipment: 0 },
            vas: data.vas || [],
            minimum_monthly: data.minimum_monthly || 0,
          });
        }
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load rate card'))
      .finally(() => setLoading(false));
  }, [selected]);

  if (!selected) return <div className="text-muted-foreground p-6 text-center">Select a merchant first.</div>;
  if (loading || !form) return <div className="text-muted-foreground p-6">Loading…</div>;

  const setNested = (group, key, value) => {
    setForm({ ...form, [group]: { ...form[group], [key]: parseFloat(value) || 0 } });
  };
  const submit = async () => {
    setSaving(true);
    try {
      await upsert3plRateCard(selected.id, form);
      toast.success('Rate card saved');
      const r = await get3plRateCard(selected.id);
      setCard(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const Sec = ({ title, children }) => (
    <div className="border border-border bg-card p-3 space-y-2">
      <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>
    </div>
  );
  const NumIn = ({ label, group, k, prefix = '$', testId }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{prefix}</span>
        <Input
          type="number" step="0.01" min="0"
          className="pl-6 font-mono"
          value={form[group][k] ?? 0}
          onChange={(e) => setNested(group, k, e.target.value)}
          data-testid={testId}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Rate Card · {selected.name}</h2>
        {card?.created_at && (
          <div className="text-[11px] text-muted-foreground">
            Current card effective {fmtDate(card.effective_from)} · saved {fmtDate(card.created_at)}
          </div>
        )}
      </div>

      <Sec title="Storage (monthly)">
        <NumIn label="Per pallet" group="storage" k="per_pallet_per_month" testId="rate-storage-pallet" />
        <NumIn label="Per bin" group="storage" k="per_bin_per_month" testId="rate-storage-bin" />
        <NumIn label="Per m³" group="storage" k="per_cubic_meter_per_month" testId="rate-storage-cbm" />
        <NumIn label="Free pallets" group="storage" k="free_pallets" prefix="#" testId="rate-storage-free" />
      </Sec>

      <Sec title="Inbound">
        <NumIn label="Per receipt" group="inbound" k="per_receipt" testId="rate-inbound-receipt" />
        <NumIn label="Per pallet" group="inbound" k="per_pallet" testId="rate-inbound-pallet" />
        <NumIn label="Per carton" group="inbound" k="per_carton" testId="rate-inbound-carton" />
      </Sec>

      <Sec title="Outbound">
        <NumIn label="Per order" group="outbound" k="per_order" testId="rate-outbound-order" />
        <NumIn label="Per pick line" group="outbound" k="per_pick_line" testId="rate-outbound-pickline" />
        <NumIn label="Per shipment" group="outbound" k="per_shipment" testId="rate-outbound-shipment" />
      </Sec>

      <div className="border border-border bg-card p-3 space-y-2">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Minimum monthly</h3>
        <div className="flex items-center gap-3">
          <div className="relative w-48">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
            <Input type="number" step="0.01" min="0" className="pl-6 font-mono"
              value={form.minimum_monthly ?? 0}
              onChange={(e) => setForm({ ...form, minimum_monthly: parseFloat(e.target.value) || 0 })}
              data-testid="rate-minimum-monthly" />
          </div>
          <div className="text-[11px] text-muted-foreground">
            Top-up charge added at invoice generation when total accruals fall below this floor.
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={submit} disabled={saving} data-testid="rate-card-save">
          {saving ? 'Saving…' : 'Save rate card'}
        </Button>
      </div>
    </div>
  );
}
